"use client";

/**
 * Right column of the enrollment-preview Sheet (Figma `10351:10686`).
 *
 * For the selected candidate this renders, top-to-bottom:
 *   - a header ("Enroll N profiles to sequence" + close),
 *   - the candidate-level recipient selector ("Selected Email Address"),
 *   - one step card per pinned step showing the FINAL per-candidate
 *     content (variables resolved, AI generated, no preview colors), with
 *     the first step carrying its absolute send ETA,
 *   - delay pills on the connectors between steps,
 *   - an autosave status footer.
 *
 * Pinned steps come from `useGetStepSetVersion` against the preview's
 * `pinned_step_set_version_number` (NOT the sequence's live step set), so a
 * mid-flight sequence edit never churns the preview. Per-step rendered
 * content comes from `candidate.steps[]` (`PreviewStepRender`).
 *
 * Recipient selection lives here (per candidate) so the chosen address can
 * be threaded into every email card's override. The address is persisted
 * as the per-step `email_to_address` override, which the backend send path
 * honours ahead of the waterfall pick and which clears the
 * "no deliverable email" fix issue.
 */

import * as React from "react";

import { PanelBackButton } from "@/components/layout/sidebar/mobile/panel-back-button";
import { Icon } from "@/components/ui/icon";
import { useGetStepSetVersion } from "@/lib/api/generated";
import { cn } from "@/lib/utils";

import { formatExecuteEta } from "../../lib/badges";
import { ISSUE_META } from "../../lib/issue-meta";
import type {
  EnrollmentPreviewCandidate,
  EnrollmentPreviewPatchRequestSetOverrides,
  PreviewStepRender,
  ValidationCode,
} from "../../lib/types";

import { DelayPill } from "./delay-pill";
import { EmailSelector, type EmailOption, type EmailType } from "./email-selector";
import { EnrollStepCard } from "./enroll-step-card";

export interface StepPaneProps {
  totalCount: number;
  selectedCandidate: EnrollmentPreviewCandidate | null;
  jobId: string;
  sequenceId: string;
  pinnedStepSetVersionNumber: number | null;
  /** Sender timezone — drives the absolute ETA line on the first step. */
  senderTz?: string | null;
  onClose: () => void;
  /** Mobile-only (ARBI-677): shared muted chevron "Back" control that
   * returns to the recipient list (step 1 of the mobile two-step flow).
   * Desktop never passes this — the panes render side-by-side there. */
  onBack?: () => void;
  patchPreview: (data: {
    set_overrides?: EnrollmentPreviewPatchRequestSetOverrides;
    request_seq?: number | null;
  }) => Promise<unknown>;
}

const EMAIL_TYPES = new Set(["automatic_email", "manual_email"]);

export function StepPane({
  totalCount,
  selectedCandidate,
  jobId,
  sequenceId,
  pinnedStepSetVersionNumber,
  senderTz,
  onClose,
  onBack,
  patchPreview,
}: StepPaneProps) {
  const stepSetQuery = useGetStepSetVersion(
    jobId,
    sequenceId,
    pinnedStepSetVersionNumber ?? 0,
    {
      query: {
        enabled: Boolean(jobId && sequenceId && pinnedStepSetVersionNumber),
      },
    },
  );

  const steps = React.useMemo(
    () =>
      (stepSetQuery.data?.steps ?? [])
        .slice()
        .sort((a, b) => (a.step_index ?? 0) - (b.step_index ?? 0)),
    [stepSetQuery.data],
  );

  // For each reply step, the step number (1-based, matching the card's
  // `stepNumber`) of the preceding new-thread email its subject is derived
  // from — so the card can say "Derived from Step 2" instead of the vague
  // "previous step".
  const replySourceByStep = React.useMemo(() => {
    const map = new Map<string, number>();
    let lastThreadRootNumber: number | null = null;
    steps.forEach((step, idx) => {
      if (!EMAIL_TYPES.has(step.type)) return;
      if (step.email_thread_mode === "reply") {
        if (lastThreadRootNumber) map.set(step.id, lastThreadRootNumber);
      } else {
        // new_thread (or any non-reply email) starts/continues a thread root.
        lastThreadRootNumber = idx + 1;
      }
    });
    return map;
  }, [steps]);

  const cellsByStep = React.useMemo(() => {
    const map = new Map<string, PreviewStepRender>();
    for (const cell of selectedCandidate?.steps ?? []) {
      map.set(cell.step_id, cell);
    }
    return map;
  }, [selectedCandidate]);

  const issuesByStep = React.useMemo(() => {
    const map = new Map<string, EnrollmentPreviewCandidate["issues"]>();
    for (const issue of selectedCandidate?.issues ?? []) {
      if (!issue.step_id) continue;
      const list = map.get(issue.step_id) ?? [];
      list.push(issue);
      map.set(issue.step_id, list);
    }
    return map;
  }, [selectedCandidate]);

  // Candidate-level issues carry no `step_id` (e.g. sender mailbox
  // disconnected, no deliverable email). They block the commit but used to
  // be silently dropped here, so the recipient showed "1 issue" with no
  // explanation. Surface them as a banner at the top of the pane.
  const candidateLevelIssues = React.useMemo(
    () => (selectedCandidate?.issues ?? []).filter((i) => !i.step_id),
    [selectedCandidate],
  );

  const hasEmailStep = steps.some((s) => EMAIL_TYPES.has(s.type));

  // Per-candidate recipient selection. Defaults to the waterfall-resolved
  // address from the preview; the recruiter can switch, add, or clear an
  // address. A present key whose value is `null` means the recruiter
  // explicitly cleared the recipient (e.g. the chosen email was deleted) —
  // distinct from an absent key, which falls back to the waterfall pick.
  const [emailByCandidate, setEmailByCandidate] = React.useState<
    Record<string, EmailOption | null>
  >({});

  const candidateId = selectedCandidate?.job_candidate_id ?? null;
  const selectedEmailOption: EmailOption | null = React.useMemo(() => {
    if (!selectedCandidate) return null;
    const id = selectedCandidate.job_candidate_id;
    if (id in emailByCandidate) {
      return emailByCandidate[id] ?? { address: "", emailType: null };
    }
    return {
      address: selectedCandidate.resolved_email ?? "",
      emailType: (selectedCandidate.resolved_email_type ?? null) as EmailType,
    };
  }, [selectedCandidate, emailByCandidate]);

  const handleSelectEmail = React.useCallback(
    (option: EmailOption | null) => {
      if (!candidateId) return;
      setEmailByCandidate((prev) => ({ ...prev, [candidateId]: option }));
    },
    [candidateId],
  );

  const firstName = selectedCandidate?.full_name.split(" ")[0] ?? "";

  return (
    <section
      className={cn(
        "flex flex-1 min-w-0 flex-col bg-surface-header",
        // ARBI-677 mobile two-step: with no candidate selected the pane
        // stays hidden below `lg` — the recipient list owns the full width.
        // Once one is selected the list hides instead and `flex-1` makes
        // this pane 100% wide.
        !selectedCandidate && "max-lg:hidden",
      )}
    >
      <header className="flex h-[60px] shrink-0 items-center gap-2 border-b border-border px-6 max-lg:px-4">
        {/* Mobile: shared muted chevron "Back" control (returns to the
            recipient list). Desktop never passes onBack, so it keeps the
            paper-plane icon and the side-by-side layout untouched. */}
        {onBack ? (
          <PanelBackButton onBack={onBack} />
        ) : (
          <Icon name="IconPaperPlane" size={18} className="text-foreground opacity-80" />
        )}
        <h2 className="flex-1 text-[18px] font-medium text-heading max-lg:min-w-0 max-lg:truncate max-lg:text-[15px]">
          Enroll {totalCount} {totalCount === 1 ? "profile" : "profiles"} to
          sequence
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close enrollment preview"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
        >
          <Icon name="IconCrossMedium" size={16} />
        </button>
      </header>

      {!selectedCandidate ? (
        <EmptyMode />
      ) : (
        <div className="enroll-dot-grid flex flex-1 min-h-0 flex-col gap-5 overflow-y-auto p-6 max-lg:p-4">
          {candidateLevelIssues.length > 0 ? (
            <CandidateIssuesBanner issues={candidateLevelIssues} />
          ) : null}

          {hasEmailStep ? (
            <EmailSelector
              profileId={selectedCandidate.profile_id}
              selectedEmail={selectedEmailOption?.address || null}
              selectedEmailType={selectedEmailOption?.emailType ?? null}
              onSelect={handleSelectEmail}
            />
          ) : null}

          {stepSetQuery.isLoading ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Loading steps…
            </p>
          ) : steps.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              This sequence has no steps.
            </p>
          ) : (
            steps.map((step, idx) => (
              <React.Fragment key={`${candidateId}:${step.id}`}>
                {idx > 0 ? <DelayPill nextStep={step} /> : null}
                <EnrollStepCard
                  jobId={jobId}
                  sequenceId={sequenceId}
                  jobCandidateId={selectedCandidate.job_candidate_id}
                  step={step}
                  stepNumber={idx + 1}
                  cell={cellsByStep.get(step.id)}
                  issues={issuesByStep.get(step.id) ?? []}
                  replySourceStepNumber={replySourceByStep.get(step.id) ?? null}
                  executeLabel={
                    idx === 0
                      ? formatExecuteEta(
                          selectedCandidate.first_step_due_at,
                          senderTz,
                        )
                      : null
                  }
                  selectedEmail={selectedEmailOption?.address || null}
                  patchPreview={patchPreview}
                />
              </React.Fragment>
            ))
          )}
        </div>
      )}

      {selectedCandidate ? (
        <footer className="flex h-[60px] shrink-0 items-center gap-1.5 border-t border-border bg-surface-header px-6 text-sm text-muted-foreground max-lg:px-4 max-lg:text-xs">
          <Icon name="IconCircleCheck" size={14} className="text-muted-foreground max-lg:shrink-0" />
          <span className="max-lg:min-w-0 max-lg:truncate">{`All changes to ${firstName}'s sequence are automatically saved`}</span>
        </footer>
      ) : null}
    </section>
  );
}

/**
 * Candidate-level issues (no `step_id`) above the step cards. Renders EVERY
 * issue — a single issue keeps the icon + title/detail row; multiple issues
 * collapse to a headline + bullet list. Blocking (`fix`) issues get the
 * premium solid-red treatment (opaque #FF2500 gradient, borderless, white
 * text/icon); warning-only banners keep the soft amber card.
 */
function CandidateIssuesBanner({
  issues = [],
}: {
  issues: EnrollmentPreviewCandidate["issues"];
}) {
  const hasFix = issues.some((i) => i.severity === "fix");
  const single = issues.length === 1;
  const firstMeta = single
    ? ISSUE_META[issues[0].code as ValidationCode]
    : undefined;

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-xl p-4",
        hasFix
          ? // Solid #ED3B3B with a slightly darker bottom stop for depth
            // (same treatment as Figma `10738:452`, recolored). No border;
            // faint white top highlight.
            "bg-[linear-gradient(180deg,#ed3b3b_0%,#d32f2f_100%)] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.11)]"
          : "border border-amber-500/30 bg-amber-500/[0.06]",
      )}
    >
      {single ? (
        <div className="flex items-start gap-2.5">
          <Icon
            name={firstMeta?.icon ?? "IconExclamationCircle"}
            size={16}
            className={cn(
              "mt-0.5 shrink-0",
              hasFix ? "text-white" : firstMeta?.color ?? "text-amber-500",
            )}
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span
              className={cn(
                "text-sm font-medium",
                hasFix ? "text-white" : "text-heading",
              )}
            >
              {firstMeta?.label ?? issues[0].code}
            </span>
            <span
              className={cn(
                "text-[13px] leading-snug",
                hasFix ? "text-white/90" : "text-muted-foreground",
              )}
            >
              {issues[0].message ?? firstMeta?.resolution ?? ""}
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2.5">
            <Icon
              name="IconExclamationCircle"
              size={16}
              className={cn(
                "shrink-0",
                hasFix ? "text-white" : "text-amber-500",
              )}
            />
            <span
              className={cn(
                "text-sm font-medium",
                hasFix ? "text-white" : "text-heading",
              )}
            >
              {issues.length} issues need attention
            </span>
          </div>
          <ul
            className={cn(
              "flex list-disc flex-col gap-1.5 pl-9",
              hasFix ? "marker:text-white/70" : "marker:text-amber-500/70",
            )}
          >
            {issues.map((issue, idx) => {
              const meta = ISSUE_META[issue.code as ValidationCode];
              return (
                <li
                  key={`${issue.code}:${idx}`}
                  className={cn(
                    "text-[13px] leading-snug",
                    hasFix ? "text-white/90" : "text-muted-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "font-medium",
                      hasFix ? "text-white" : "text-heading",
                    )}
                  >
                    {meta?.label ?? issue.code}
                  </span>
                  {" — "}
                  {issue.message ?? meta?.resolution ?? ""}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function EmptyMode() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-10 text-center text-sm text-muted-foreground">
      <Icon name="IconPaperPlane" size={28} className="mb-3 opacity-40" />
      <p>Select a recipient to preview and edit their messages.</p>
    </div>
  );
}
